import {
  AGE_GROUP_LABELS,
  AGE_GROUPS,
  NEXT_BELT,
  type AgeGroup,
  type CategoryDto,
  type ImportResultDto,
  type TechniqueDto,
} from '@bjj/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { api } from '../api/client';
import { useApiMutation, useCategories, useReorder, useTechniques, useVideos } from '../api/hooks';
import { DataTable, type Column } from '../components/DataTable';
import { PrerequisiteSelect } from '../components/PrerequisiteSelect';
import { SortableTable } from '../components/SortableTable';
import {
  Badge,
  Card,
  ConfirmButton,
  ErrorMessage,
  Field,
  Loading,
  PageHeader,
} from '../components/ui';

const CURRICULUM_KEYS = [
  ['admin', 'techniques'],
  ['admin', 'categories'],
  ['admin', 'overview'],
];

const byOrder = <T extends { order: number; name: string }>(a: T, b: T) =>
  a.order - b.order || a.name.localeCompare(b.name);

/** The order value that puts an item after everything in `items`. */
const endOrder = (items: { order: number }[]) =>
  items.reduce((max, item) => Math.max(max, item.order), 0) + 10;

export function CurriculumPage() {
  const techniques = useTechniques();
  const categories = useCategories();
  const [filter, setFilter] = useState('');
  const toggle = useApiMutation<{ id: string; active: boolean }>(
    ({ id, active }) => ({
      path: `/admin/techniques/${id}`,
      method: 'PATCH',
      body: { active },
    }),
    CURRICULUM_KEYS,
  );
  const reorder = useReorder(
    ['admin', 'techniques'],
    (categoryId) => `/admin/categories/${categoryId}/technique-order`,
    CURRICULUM_KEYS,
  );
  const reorderCategories = useReorder(['admin', 'categories'], () => '/admin/categories/order', [
    ['admin', 'overview'],
  ]);
  const [adding, setAdding] = useState(false);
  // Kids and adults have separate curricula; the owner edits one at a time.
  const [search, setSearch] = useSearchParams();
  const ageGroup: AgeGroup = search.get('group') === 'kids' ? 'kids' : 'adults';
  const allCategories = [...(categories.data ?? [])].sort(byOrder);
  const sortedCategories = allCategories.filter((c) => c.ageGroup === ageGroup);
  const groupTechniques = (techniques.data ?? []).filter((t) =>
    sortedCategories.some((c) => c.id === t.categoryId),
  );
  const catName = new Map(sortedCategories.map((c) => [c.id, c.name]));
  const byId = new Map((techniques.data ?? []).map((t) => [t.id, t]));
  // Techniques per category, in display order.
  const groups = techniques.data
    ? sortedCategories.map((c) => {
        const rows = techniques.data.filter((t) => t.categoryId === c.id).sort(byOrder);
        return {
          id: c.id,
          name: c.name,
          label: <CategoryHeading category={c} count={rows.length} />,
          rows,
          empty: 'No techniques in this category yet.',
        };
      })
    : undefined;
  const position = new Map(groups?.flatMap((g) => g.rows.map((t, i) => [t.id, i + 1])));
  const filtering = filter.trim() !== '';
  const rows = groups
    ?.flatMap((g) => g.rows)
    .filter((t) =>
      `${t.name} ${catName.get(t.categoryId) ?? ''}`.toLowerCase().includes(filter.toLowerCase()),
    );
  const columns: Column<TechniqueDto>[] = [
    { key: 'order', header: '#', label: 'Position', width: 50, render: (t) => position.get(t.id) },
    {
      key: 'name',
      header: 'Technique',
      render: (t) => <Link to={`/curriculum/${t.id}`}>{t.name}</Link>,
    },
    { key: 'cat', header: 'Category', render: (t) => catName.get(t.categoryId) ?? '—' },
    {
      key: 'pre',
      header: 'Prerequisites',
      render: (t) =>
        t.prerequisiteIds.length ? (
          t.prerequisiteIds.map((p) => byId.get(p)?.name ?? '?').join(', ')
        ) : (
          <span className="muted">None</span>
        ),
    },
    {
      key: 'video',
      header: 'Video',
      render: (t) => (t.videoAssetId ? 'Yes' : <span className="muted">No</span>),
    },
    {
      key: 'status',
      header: 'Status',
      render: (t) => <Badge value={t.active ? 'active' : 'inactive'} />,
    },
    {
      key: 'actions',
      header: '',
      render: (t) => (
        <button
          type="button"
          className="btn small"
          onClick={() => toggle.mutate({ id: t.id, active: !t.active })}
        >
          {t.active ? 'Deactivate' : 'Activate'}
        </button>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Curriculum"
        subtitle={
          techniques.data
            ? `${AGE_GROUP_LABELS[ageGroup]}, toward ${NEXT_BELT[ageGroup].name}: ${groupTechniques.filter((t) => t.active).length} active of ${groupTechniques.length} techniques`
            : undefined
        }
        actions={
          <div className="btn-row">
            <button type="button" className="btn" onClick={() => setAdding(true)}>
              New category
            </button>
            <Link
              className="btn primary"
              to={
                sortedCategories[0]
                  ? `/curriculum/new?category=${sortedCategories[0].id}`
                  : '/curriculum/new'
              }
            >
              New technique
            </Link>
          </div>
        }
      />
      <div className="btn-row" role="group" aria-label="Curriculum" style={{ marginBottom: 16 }}>
        {AGE_GROUPS.map((g) => (
          <button
            key={g}
            type="button"
            className={g === ageGroup ? 'btn primary' : 'btn'}
            aria-pressed={g === ageGroup}
            onClick={() => {
              setAdding(false);
              setSearch(g === 'kids' ? { group: 'kids' } : {});
            }}
          >
            {AGE_GROUP_LABELS[g]} · {NEXT_BELT[g].name}
          </button>
        ))}
      </div>
      {adding || (categories.data && sortedCategories.length === 0) ? (
        <NewCategoryForm
          key={ageGroup}
          ageGroup={ageGroup}
          order={endOrder(sortedCategories)}
          onClose={sortedCategories.length ? () => setAdding(false) : undefined}
        />
      ) : null}
      <Card
        actions={
          <input
            type="search"
            aria-label="Filter techniques"
            placeholder="Filter…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        }
      >
        <ErrorMessage
          error={techniques.error ?? toggle.error ?? reorder.error ?? reorderCategories.error}
        />
        <p className="muted">
          {filtering
            ? 'Clear the filter to change the order.'
            : 'Drag a handle to change the order students see: beside a technique to move it within its category, or in a category heading to move the whole category.'}
        </p>
        {filtering ? (
          <DataTable
            rows={rows}
            rowKey={(t) => t.id}
            empty="No matching techniques."
            columns={columns}
          />
        ) : (
          <SortableTable
            groups={groups}
            rowKey={(t) => t.id}
            rowLabel={(t) => t.name}
            empty="No categories yet."
            columns={columns.filter((c) => c.key !== 'cat')}
            onReorder={(groupId, ids) => reorder.mutate({ groupId, ids })}
            // The server orders every category at once; the other group's keep their order.
            onReorderGroups={(ids) =>
              reorderCategories.mutate({
                groupId: 'all',
                ids: [
                  ...ids,
                  ...allCategories.filter((c) => c.ageGroup !== ageGroup).map((c) => c.id),
                ],
              })
            }
          />
        )}
      </Card>
    </>
  );
}

function NewCategoryForm({
  ageGroup,
  order,
  onClose,
}: {
  ageGroup: AgeGroup;
  order: number;
  onClose?: () => void;
}) {
  const [name, setName] = useState('');
  const create = useApiMutation<void>(
    () => ({ path: '/admin/categories', method: 'POST', body: { name, order, ageGroup } }),
    CURRICULUM_KEYS,
  );
  return (
    <Card title={`New ${AGE_GROUP_LABELS[ageGroup].toLowerCase()} category`}>
      {onClose ? null : (
        <p className="muted">
          {ageGroup === 'kids'
            ? 'Start the kids curriculum by adding a category, e.g. Movement or Takedowns.'
            : 'Start by adding a category, e.g. Guard or Takedowns.'}
        </p>
      )}
      <ErrorMessage error={create.error} />
      <form
        className="btn-row"
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate(undefined, {
            onSuccess: () => {
              setName('');
              onClose?.();
            },
          });
        }}
      >
        <input
          type="text"
          aria-label="Category name"
          placeholder="e.g. Guard"
          required
          maxLength={80}
          value={name}
          onChange={(e) => setName(e.target.value)}
          style={{ maxWidth: 320 }}
        />
        <button className="btn primary" type="submit" disabled={create.isPending}>
          Add category
        </button>
        {onClose ? (
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
        ) : null}
      </form>
    </Card>
  );
}

/** A category's heading in the curriculum table: its name and the category actions. */
function CategoryHeading({ category, count }: { category: CategoryDto; count: number }) {
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState(category.name);
  const update = useApiMutation<Partial<CategoryDto>>(
    (patch) => ({ path: `/admin/categories/${category.id}`, method: 'PATCH', body: patch }),
    CURRICULUM_KEYS,
  );
  const remove = useApiMutation<void>(
    () => ({ path: `/admin/categories/${category.id}`, method: 'DELETE' }),
    CURRICULUM_KEYS,
  );
  const cancel = () => {
    setName(category.name);
    setRenaming(false);
  };
  return (
    <>
      {renaming ? (
        <form
          className="btn-row group-rename"
          onSubmit={(e) => {
            e.preventDefault();
            update.mutate({ name }, { onSuccess: () => setRenaming(false) });
          }}
        >
          <input
            type="text"
            aria-label={`New name for ${category.name}`}
            required
            maxLength={80}
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === 'Escape' && cancel()}
            autoFocus
          />
          <button className="btn small primary" type="submit">
            Save
          </button>
          <button className="btn small" type="button" onClick={cancel}>
            Cancel
          </button>
        </form>
      ) : (
        <span className="group-name">{category.name}</span>
      )}
      {category.active ? null : <Badge value="hidden" />}
      <span className="group-count">
        {count} {count === 1 ? 'technique' : 'techniques'}
      </span>
      <div className="btn-row group-actions">
        <Link className="btn small" to={`/curriculum/new?category=${category.id}`}>
          Add technique
        </Link>
        {renaming ? null : (
          <button type="button" className="btn small" onClick={() => setRenaming(true)}>
            Rename
          </button>
        )}
        <button
          type="button"
          className="btn small"
          title={
            category.active
              ? 'Hide this category and its techniques from students'
              : 'Show this category to students again'
          }
          onClick={() => update.mutate({ active: !category.active })}
        >
          {category.active ? 'Hide' : 'Show'}
        </button>
        {count === 0 ? (
          <ConfirmButton confirmLabel="Delete category?" onConfirm={() => remove.mutate()}>
            Delete
          </ConfirmButton>
        ) : null}
      </div>
      <ErrorMessage error={update.error ?? remove.error} />
    </>
  );
}

// The key is generated by the server and only used in CSV files, so the form never shows it.
type Draft = Omit<TechniqueDto, 'id' | 'key' | 'updatedAt'>;
const EMPTY: Draft = {
  name: '',
  description: '',
  categoryId: '',
  order: 0,
  prerequisiteIds: [],
  videoAssetId: null,
  active: false,
};
const toDraft = (t: TechniqueDto): Draft => ({
  name: t.name,
  description: t.description,
  categoryId: t.categoryId,
  order: t.order,
  prerequisiteIds: t.prerequisiteIds,
  videoAssetId: t.videoAssetId,
  active: t.active,
});

export function TechniqueEditPage() {
  const { id } = useParams();
  const isNew = id === 'new';
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const techniques = useTechniques();
  const categories = useCategories();
  const videos = useVideos();
  const existing = techniques.data?.find((t) => t.id === id);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);
  const remove = useApiMutation<void>(
    () => ({ path: `/admin/techniques/${id}`, method: 'DELETE' }),
    CURRICULUM_KEYS,
  );

  if (techniques.isLoading || categories.isLoading) return <Loading />;
  if (!isNew && !existing) return <ErrorMessage error={new Error('Technique not found')} />;
  const value: Draft =
    draft ??
    (existing
      ? toDraft(existing)
      : {
          ...EMPTY,
          categoryId:
            categories.data?.find((c) => c.id === search.get('category'))?.id ??
            [...(categories.data ?? [])].sort(byOrder)[0]?.id ??
            '',
        });
  const set = (patch: Partial<Draft>) => setDraft({ ...value, ...patch });

  async function save() {
    setSaving(true);
    setError(null);
    try {
      // New techniques, and ones moved to another category, go to the end of that category.
      const moved = isNew || value.categoryId !== existing?.categoryId;
      const siblings = (techniques.data ?? []).filter(
        (t) => t.categoryId === value.categoryId && t.id !== id,
      );
      const body = {
        ...value,
        order: moved ? endOrder(siblings) : value.order,
        videoAssetId: value.videoAssetId || null,
      };
      const saved = isNew
        ? await api.request<TechniqueDto>('/admin/techniques', { method: 'POST', body })
        : await api.request<TechniqueDto>(`/admin/techniques/${id}`, { method: 'PATCH', body });
      await Promise.all(CURRICULUM_KEYS.map((k) => qc.invalidateQueries({ queryKey: k })));
      navigate(`/curriculum/${saved.id}`, { replace: true });
      setDraft(null);
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <PageHeader
        title={isNew ? 'New technique' : value.name || 'Technique'}
        subtitle={<Link to="/curriculum">← Back to curriculum</Link>}
      />
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <Card title="Details">
          <ErrorMessage error={error} />
          <div className="form-row">
            <Field label="Name">
              {(fid) => (
                <input
                  id={fid}
                  type="text"
                  required
                  maxLength={120}
                  value={value.name}
                  onChange={(e) => set({ name: e.target.value })}
                />
              )}
            </Field>
            <Field label="Category">
              {(fid) => (
                <select
                  id={fid}
                  required
                  value={value.categoryId}
                  onChange={(e) => set({ categoryId: e.target.value })}
                >
                  <option value="" disabled>
                    Choose a category
                  </option>
                  {AGE_GROUPS.map((g) => {
                    const options = [...(categories.data ?? [])]
                      .filter((c) => c.ageGroup === g)
                      .sort(byOrder);
                    return options.length ? (
                      <optgroup key={g} label={`${AGE_GROUP_LABELS[g]} curriculum`}>
                        {options.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name}
                          </option>
                        ))}
                      </optgroup>
                    ) : null;
                  })}
                </select>
              )}
            </Field>
          </div>
          <Field label="Description">
            {(fid) => (
              <textarea
                id={fid}
                maxLength={5000}
                value={value.description}
                onChange={(e) => set({ description: e.target.value })}
              />
            )}
          </Field>
          <label className="checkbox">
            <input
              type="checkbox"
              checked={value.active}
              onChange={(e) => set({ active: e.target.checked })}
            />{' '}
            Active (students see it, it can be graded and it counts towards progress; turning it off
            keeps everyone&rsquo;s past grades)
          </label>
        </Card>
        <Card title="Prerequisites">
          <p className="muted">Students need an A on these before this technique can be graded.</p>
          <PrerequisiteSelect
            techniqueId={isNew ? null : id!}
            techniques={techniques.data ?? []}
            value={value.prerequisiteIds}
            onChange={(ids) => set({ prerequisiteIds: ids })}
          />
        </Card>
        <Card title="Video">
          <Field label="Video" hint={<Link to="/videos">Manage videos</Link>}>
            {(fid) => (
              <select
                id={fid}
                value={value.videoAssetId ?? ''}
                onChange={(e) => set({ videoAssetId: e.target.value || null })}
              >
                <option value="">No video</option>
                {videos.data?.items.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.title}
                  </option>
                ))}
              </select>
            )}
          </Field>
        </Card>
        <div className="btn-row">
          <button className="btn primary" type="submit" disabled={saving}>
            {saving ? 'Saving…' : isNew ? 'Create technique' : 'Save changes'}
          </button>
          {!isNew ? (
            <ConfirmButton
              confirmLabel="Delete technique?"
              onConfirm={() =>
                remove.mutate(undefined, { onSuccess: () => navigate('/curriculum') })
              }
            >
              Delete
            </ConfirmButton>
          ) : null}
        </div>
        <ErrorMessage error={remove.error} />
      </form>
    </>
  );
}

export function ImportExportPage() {
  const qc = useQueryClient();
  const [csv, setCsv] = useState('');
  const [result, setResult] = useState<ImportResultDto | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  async function run(dryRun: boolean) {
    setBusy(true);
    setError(null);
    try {
      setResult(
        await api.request<ImportResultDto>('/admin/curriculum/import', {
          method: 'POST',
          body: { csv, dryRun },
        }),
      );
      if (!dryRun)
        await Promise.all(CURRICULUM_KEYS.map((k) => qc.invalidateQueries({ queryKey: k })));
    } catch (err) {
      setResult(null);
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  async function exportCsv() {
    const text = await api.request<string>('/admin/curriculum/export', { text: true });
    const url = URL.createObjectURL(new Blob([text], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `curriculum-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <>
      <PageHeader
        title="Import / Export"
        subtitle="Bulk-edit the curriculum as CSV. Techniques are matched by key, so progress is never lost."
      />
      <Card title="Export">
        <button type="button" className="btn" onClick={() => void exportCsv()}>
          Download curriculum CSV
        </button>
      </Card>
      <Card title="Import">
        <p className="muted">
          Columns:{' '}
          <span className="mono">
            key,name,category,order,description,prerequisites,videoUrl,active
          </span>
          . Keys are short ids like <span className="mono">armbar-from-mount</span>; export first to
          see the existing ones. Separate prerequisite keys with <span className="mono">|</span>.
          Video links must be YouTube URLs. Older files with a{' '}
          <span className="mono">published</span> column still import.
        </p>
        <Field label="CSV file">
          {(id) => (
            <input
              id={id}
              type="file"
              accept=".csv,text/csv"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (file) setCsv(await file.text());
              }}
            />
          )}
        </Field>
        <Field label="…or paste CSV">
          {(id) => (
            <textarea
              id={id}
              value={csv}
              onChange={(e) => setCsv(e.target.value)}
              rows={8}
              className="mono"
            />
          )}
        </Field>
        <div className="btn-row">
          <button
            type="button"
            className="btn"
            disabled={!csv || busy}
            onClick={() => void run(true)}
          >
            Validate (dry run)
          </button>
          <button
            type="button"
            className="btn primary"
            disabled={!csv || busy}
            onClick={() => void run(false)}
          >
            Import
          </button>
        </div>
        <div style={{ marginTop: 12 }}>
          <ErrorMessage error={error} />
          {result ? (
            result.errors.length ? (
              <div className="alert error">
                {result.errors.length} problem(s):
                <ul>
                  {result.errors.map((e, i) => (
                    <li key={i}>
                      Line {e.line}: {e.message}
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <div className="alert success" role="status">
                {result.dryRun ? 'Dry run OK: ' : 'Imported: '}
                {result.created} created, {result.updated} updated, {result.categoriesCreated} new
                categories.
              </div>
            )
          ) : null}
        </div>
      </Card>
    </>
  );
}
